import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ImageCoverageRecord } from 'apis/merchant-gateway/insights/image-coverage/types';
import { InsightEntity } from 'modules/insights-v2/types';

import { useImageCoverageInsights } from './hooks/useImageCoverageInsights';
import { ImageCoverageTableColumnIds } from './types';
import InfiniteTable from '../tables/InfiniteTable';
import { SortOrder, SortState } from '../tables/types';

const TABLE_COLUMN_WIDTH = 300;

export type ImageCoverageDetailsProps = {
  insightEntity: InsightEntity;
  onClose: () => void;
  isOpened: boolean;
};

const StyledModalContent = styled(ModalContent)`
  height: 460px;
  display: flex;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const StyledSpinner = styled(Spinner)`
  vertical-align: middle;
  margin-inline-start: 8px;
`;

export const ImageCoverageDetails = ({ insightEntity, isOpened, onClose }: ImageCoverageDetailsProps) => {
  const intl = useIntl();
  const [sort, setSort] = useState<SortState<ImageCoverageTableColumnIds>>({
    sortBy: ImageCoverageTableColumnIds.COVERAGE,
    sortOrder: SortOrder.ASC,
  });

  const {
    imageCoverageRecords,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useImageCoverageInsights({ insightEntity, sortOrder: sort.sortOrder, sortBy: sort.sortBy });

  const itemsColumnLabels = {
    venue: intl.formatMessage({ id: 'insights.menu.image-coverage.section.modal.tableColumns.venue' }),
    category: intl.formatMessage({ id: 'insights.menu.image-coverage.section.modal.tableColumns.category' }),
  };

  const itemsColumnId =
    insightEntity.type === 'venue' ? ImageCoverageTableColumnIds.CATEGORY_NAME : ImageCoverageTableColumnIds.VENUE_SLUG;
  const itemsColumnLabel = insightEntity.type === 'venue' ? itemsColumnLabels.category : itemsColumnLabels.venue;

  return (
    <Modal open={isOpened} onDismiss={onClose} sizePreset="centered:large" disableContentPadding>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="insights.menu.image-coverage.section.modal.heading" />
        {isFetching && <StyledSpinner size="small" />}
      </ModalHeader>
      <StyledModalContent disablePadding>
        <InfiniteTable
          items={imageCoverageRecords}
          fetchingNextPage={isFetchingNextPage}
          fetchingPreviousPage={isFetchingPreviousPage}
          onFetchNextPage={hasNextPage ? fetchNextPage : undefined}
          onFetchPreviousPage={hasPreviousPage ? fetchPreviousPage : undefined}
          sort={sort}
          columns={[
            {
              id: itemsColumnId,
              label: itemsColumnLabel,
              width: TABLE_COLUMN_WIDTH,
              enableSort: true,
              handleSort: (sortOrder: SortOrder) => setSort({ sortBy: itemsColumnId, sortOrder }),
              render: (record: ImageCoverageRecord) => <span>{record.secondaryLabel}</span>,
            },
            {
              id: ImageCoverageTableColumnIds.COVERAGE,
              label: intl.formatMessage({
                id: 'insights.menu.image-coverage.section.modal.tableColumns.image-coverage',
              }),
              width: TABLE_COLUMN_WIDTH,
              enableSort: true,
              handleSort: (sortOrder: SortOrder) =>
                setSort({ sortBy: ImageCoverageTableColumnIds.COVERAGE, sortOrder }),
              render: (record: ImageCoverageRecord) => <span>{Math.floor(record.coverage * 100)}%</span>,
            },
          ]}
        />
      </StyledModalContent>
      <StyledModalFooter>
        <Button size="small" variant="lightBlue" onClick={onClose}>
          <FormattedMessage id="insights.menu.image-coverage.section.modal.close" />
        </Button>
      </StyledModalFooter>
    </Modal>
  );
};
